import { WarningFilled } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-layout';
import type { TableColumnsType } from 'antd';
import { Badge, Button, DatePicker, Table } from 'antd';
import Title from 'antd/es/typography/Title';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '../../../../constants';
import {
  StaffFinanceBatchContainerGlPostingSummaryFieldsFragment,
  StaffFinanceBatchContainerGlPostingSummaryGlSummaryFieldsFragment,
  StaffFinanceBatchContainerGlPostingSummaryPostingAttemptFieldsFragment
} from '../../../../generated';
import { downloadCsv } from '../../../shared/json-to-csv';
import { jsonToCsvFinanceBatch } from './finance-batch-json-to-csv';
import { FormatDate } from '../../../../constants';


const statusRenderers = {
  TRANSMITTED: () => (
    <div style={{ textAlign: 'center' }}>
      <WarningFilled style={{ fontSize: '30px', color: '#FFD700' }} />
    </div>
  ),
  notxn: () => null
} as const;

const renderSuccess = (isSuccess: boolean | undefined, status: string) => {
  const statusRenderer = statusRenderers[status as keyof typeof statusRenderers];
  if (statusRenderer) {
    return statusRenderer();
  }
  return isSuccess !== undefined ? <Badge status={isSuccess ? 'success' : 'error'} text={isSuccess ? 'Success' : 'Error'} /> : null;
};

const glSummaryColumns: TableColumnsType<StaffFinanceBatchContainerGlPostingSummaryGlSummaryFieldsFragment> = [
  { title: 'Accounting Type', dataIndex: 'accountingType', key: 'accountingType' },
  { title: 'GL Account ID', dataIndex: 'glAccountId', key: 'glAccountId' },
  { title: 'Amount', dataIndex: 'amount', key: 'amount' }
];

const postingAttemptColumns: TableColumnsType<StaffFinanceBatchContainerGlPostingSummaryPostingAttemptFieldsFragment> = [
  { title: 'Attempted At', dataIndex: ['attemptData', 'attemptedAt'], key: 'attemptedAt', render: (text: string) => FormatDate(text, DATE_TIME_FORMAT) },
  { title: 'Status', dataIndex: ['attemptData', 'status'], key: 'status' },
  { title: 'Responded At', dataIndex: ['responseData', 'respondedAt'], key: 'respondedAt', render: (text: string) => FormatDate(text, DATE_TIME_FORMAT) },
  { title: 'GL Batch Number', dataIndex: ['responseData', 'glBatchNumber'], key: 'glBatchNumber' },
  { title: 'Journal Entry Number', dataIndex: ['responseData', 'jEntryNumber'], key: 'jEntryNumber' },
  {
    title: 'Success',
    dataIndex: ['responseData', 'isSuccess'],
    key: 'isSuccess',
    render: (isSuccess, record: StaffFinanceBatchContainerGlPostingSummaryPostingAttemptFieldsFragment) => renderSuccess(isSuccess, record.attemptData.status)
  },
  { title: 'Error Message', dataIndex: ['responseData', 'errorMessage'], key: 'errorMessage' }
];

const expandedRowRender = (record: StaffFinanceBatchContainerGlPostingSummaryFieldsFragment) => (
  <>
    <h4>GL Summary</h4>
    <Table<StaffFinanceBatchContainerGlPostingSummaryGlSummaryFieldsFragment>
      columns={glSummaryColumns}
      dataSource={record.glSummary.filter((item) => item !== null)}
      pagination={false}
      rowKey={(record) => record.glAccountId}
    />
    <h4>Posting Attempts</h4>
    <Table<StaffFinanceBatchContainerGlPostingSummaryPostingAttemptFieldsFragment>
      columns={postingAttemptColumns}
      dataSource={(record.postingAttempt ?? []).filter((item) => item !== null)}
      pagination={false}
      rowKey={(record) => record.createdUser}
    />
  </>
);

const columns: TableColumnsType<StaffFinanceBatchContainerGlPostingSummaryFieldsFragment> = [
  { title: 'Transaction Date', dataIndex: 'glTransactionDate', key: 'glTransactionDate', render: (text: string) => FormatDate(text) },
  { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt', render: (text: string) => FormatDate(text, DATE_TIME_FORMAT) },
  { title: 'Updated At', dataIndex: 'updatedAt', key: 'updatedAt', render: (text: string) => FormatDate(text, DATE_TIME_FORMAT) }
];

interface FinanceBatchProps {
  data: StaffFinanceBatchContainerGlPostingSummaryFieldsFragment[];
  setFilterDates: (dates: { startDate: string | null; endDate: string | null }) => void;
  filterDates: { startDate: string | null; endDate: string | null } | null;
}

export const FinanceBatch: React.FC<FinanceBatchProps> = (props) => {
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(props.filterDates?.startDate ? dayjs(props.filterDates.startDate) : null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(props.filterDates?.endDate ? dayjs(props.filterDates.endDate) : null);

  const sortedData = useMemo(() => {
    return [...(props.data ?? [])].sort((a, b) => {
      return dayjs(a.glTransactionDate).isBefore(dayjs(b.glTransactionDate)) ? 1 : -1;
    });
  }, [props.data]);

  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    setStartDate(date);
    if (endDate?.isBefore(date, 'day')) {
      console.log('End date is before start date');
      setEndDate(null);
    }
  };

  const handleEndDateChange = (date: dayjs.Dayjs | null) => {
    setEndDate(date);
  };

  const disabledEndDate = (current: dayjs.Dayjs) => {
    return startDate ? current.isBefore(startDate, 'day') : false;
  };

  const handleDateFilter = () => {
    props.setFilterDates({
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null
    });
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    props.setFilterDates({ startDate: null, endDate: null });
  };

  return (
    <>
      <PageHeader>
        <div className="flex flex-col" style={{ marginBottom: '30px' }}>
          <div id="leftTitle" style={{ display: 'inline', float: 'left' }}>
            <Title level={3} style={{ display: 'inline' }}>
              Finance Batch
            </Title>
          </div>
        </div>
      </PageHeader>

      <div className="flex justify-between items-center">
        <div className="self-start my-4 flex">
          <div className="mb-2">
            <span className="mr-2">Start Date:</span>
            <DatePicker
              format={(value) => value.format(DATE_FORMAT).toUpperCase()}
              placeholder="Start Date"
              className="mr-2"
              onChange={handleStartDateChange}
              value={startDate}
            />
          </div>
          <div className="mb-2">
            <span className="mr-2">End Date:</span>
            <DatePicker
              format={(value) => value.format(DATE_FORMAT).toUpperCase()}
              placeholder="End Date"
              className="mr-2"
              onChange={handleEndDateChange}
              value={endDate}
              disabledDate={disabledEndDate}
            />
          </div>
          <div className="inline-flex gap-2">
            <Button type="primary" onClick={handleDateFilter}>
              Filter
            </Button>
            <Button onClick={handleReset}>Reset</Button>
          </div>
        </div>

        <div>
          <Button
            type="primary"
            onClick={() => {
              const financeRecords = props.data.flatMap((item) =>
                item.glSummary.map((glSummary: any) => ({
                  accountingType: glSummary?.accountingType,
                  glAccountId: glSummary?.glAccountId,
                  amount: glSummary?.amount,
                  transactionDate: item.glTransactionDate,
                  createdAt: item.createdAt,
                  updatedAt: item.updatedAt,
                  journalEntryNumber: String(item.postingAttempt?.[0]?.responseData?.jEntryNumber ?? ''),
                  glBatchNumber: item.postingAttempt?.[0]?.responseData?.glBatchNumber ?? '',
                  status: item.postingAttempt?.[0]?.attemptData?.status ?? '',
                  respondedAt: item.postingAttempt?.[0]?.responseData?.respondedAt ?? ''
                }))
              );
              const csvData = jsonToCsvFinanceBatch(financeRecords, startDate?.format('YYYY-MM-DD') ?? '', endDate?.format('YYYY-MM-DD') ?? '');
              downloadCsv(csvData, 'finance-batch.csv');
            }}
            disabled={!props?.data || props.data.length === 0}
          >
            Download CSV File
          </Button>
        </div>
      </div>
      <Table<StaffFinanceBatchContainerGlPostingSummaryFieldsFragment>
        className="w-full border border-[#f0f0f0] rounded-[10px] p-[15px] max-w-full border-solid"
        columns={columns}
        expandable={{ expandedRowRender }}
        dataSource={sortedData}
        rowKey={(record) => record.id}
        size="middle"
        rowHoverable={false}
      />
    </>
  );
};
